import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { initializeAuth } from "src/store/authSlice";
import Dashboard from "./components/Dashboard";
import AppLayout from "./components/layout/AppLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleGuard from "./components/RoleGuard";
import { Constants } from "./constant";
import { Permission, ServiceDomain } from "./lib/roles";
import AppFaqPanel from "./routes/AppFaqPanel";
import AppReleaseVersionsPanel from "./routes/AppReleaseVersionsPanel";
import BroadcastsComposePanel from "./routes/BroadcastsComposePanel";
import BroadcastsPanel from "./routes/BroadcastsPanel";
import DynamicLinksPanel from "./routes/DynamicLinksPanel";
import FeatureFlagsPanel from "./routes/FeatureFlagsPanel";
import HappeningNowComposeCarouselPanel from "./routes/HappeningNowComposeCarouselPanel";
import HappeningNowPanel from "./routes/HappeningNowPanel";
import ImageHostingPanel from "./routes/ImageHostingPanel";
import Iv3MobilePanel from "./routes/Iv3MobilePanel";
import Login from "./routes/Login";
import PortalNotificationsComposePanel from "./routes/PortalNotificationsComposePanel";
import PortalNotificationsPanel from "./routes/PortalNotificationsPanel";
import Settings from "./routes/Settings";
import type { AppDispatch } from "./store";

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  return (
    <Routes>
      <Route path={Constants.LOGIN_ROUTE} element={<Login />} />
      <Route
        path="settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="feature-flags"
          element={
            <RoleGuard
              service={ServiceDomain.FeatureFlags}
              minServiceLevel="read"
            >
              <FeatureFlagsPanel />
            </RoleGuard>
          }
        />
        <Route
          path="app-releases"
          element={
            <RoleGuard permission={Permission.VIEW_APP_VERSION}>
              <AppReleaseVersionsPanel />
            </RoleGuard>
          }
        />
        <Route
          path="iv3-mobile"
          element={
            <RoleGuard permission={Permission.VIEW_MOBILE_TRACKING}>
              <Iv3MobilePanel />
            </RoleGuard>
          }
        />
        <Route
          path="app-faq"
          element={
            <RoleGuard service={ServiceDomain.AppFaq} minServiceLevel="read">
              <AppFaqPanel />
            </RoleGuard>
          }
        />
        <Route
          path="dynamic-links"
          element={
            <RoleGuard
              service={ServiceDomain.DynamicLinks}
              minServiceLevel="read"
            >
              <DynamicLinksPanel />
            </RoleGuard>
          }
        />
        <Route
          path="happening-now"
          element={
            <RoleGuard
              service={ServiceDomain.HappeningNow}
              minServiceLevel="read"
            >
              <HappeningNowPanel />
            </RoleGuard>
          }
        />
        <Route
          // Legacy classic composer path. The classic composer has been
          // retired; keep the URL working by redirecting bookmarks and deep
          // links to the new carousel composer.
          path="happening-now/create"
          element={
            <Navigate to={Constants.HAPPENING_NOW_COMPOSE_ROUTE} replace />
          }
        />
        <Route
          path="happening-now/compose"
          element={
            <RoleGuard permission={Permission.MANAGE_HAPPENING_NOW}>
              <HappeningNowComposeCarouselPanel />
            </RoleGuard>
          }
        />
        <Route
          path="broadcasts"
          element={
            <RoleGuard
              service={ServiceDomain.Broadcasts}
              minServiceLevel="read"
            >
              <BroadcastsPanel />
            </RoleGuard>
          }
        />
        <Route
          path="broadcasts/create"
          element={
            <RoleGuard
              service={ServiceDomain.Broadcasts}
              minServiceLevel="manage"
            >
              <BroadcastsComposePanel />
            </RoleGuard>
          }
        />
        <Route
          path="portal-notifications"
          element={
            <RoleGuard
              service={ServiceDomain.PortalNotifications}
              minServiceLevel="read"
            >
              <PortalNotificationsPanel />
            </RoleGuard>
          }
        />
        <Route
          path="portal-notifications/compose"
          element={
            <RoleGuard
              service={ServiceDomain.PortalNotifications}
              minServiceLevel="write"
            >
              <PortalNotificationsComposePanel />
            </RoleGuard>
          }
        />
        <Route
          path="image-hosting"
          element={
            <RoleGuard
              service={ServiceDomain.ImageHosting}
              minServiceLevel="read"
            >
              <ImageHostingPanel />
            </RoleGuard>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
